/**
 * Configuration Validation
 *
 * Runtime validators that check for plugin-specific properties in config
 * and throw helpful errors if the required plugin is not loaded.
 *
 * This catches common mistakes like using `editable: true` without EditingPlugin.
 *
 * Uses a static registry of known plugin-owned properties to detect when users
 * configure features that require plugins they haven't loaded.
 */

import type { BaseGridPlugin, PluginManifest, PluginPropertyDefinition } from '../plugin';
import type { ColumnConfig, GridConfig } from '../types';
import {
    CONFIG_RULE_ERROR,
    CONFIG_RULE_WARN,
    INCOMPATIBLE_PLUGINS,
    MISSING_DEPENDENCY,
    MISSING_PLUGIN,
    MISSING_PLUGIN_CONFIG,
    OPTIONAL_DEPENDENCY,
    debugDiagnostic,
    throwDiagnostic,
    warnDiagnostic,
} from './diagnostics';
import { isDevelopment } from './utils';

/**
 * Internal property definition with plugin name attached.
 * Extends PluginPropertyDefinition with required pluginName for validation.
 */
interface InternalPropertyDefinition extends PluginPropertyDefinition {
  pluginName: string;
}

// #region Known Properties Registry
/**
 * Static registry of known plugin-owned column properties.
 * This enables detection of plugin properties even when the plugin isn't loaded.
 * Properties defined here allow helpful error messages when plugins are missing.
 *
 * ## Why This Exists (The Validation Paradox)
 *
 * We need to detect when a developer uses a plugin-owned property (like `editable`)
 * but forgets to add the plugin. However, if the plugin isn't loaded, we can't
 * read its manifest! The manifest only exists when the plugin class is imported.
 *
 * This static registry solves that: it's a "well-known properties" list that exists
 * independently of whether plugins are loaded.
 *
 * ## When Adding New Plugin-Owned Properties
 *
 * 1. **Always**: Add to the plugin's manifest `ownedProperties` (documentation, lives with plugin)
 * 2. **Optionally**: Add here if you want "forgot to add plugin" detection for that property
 *
 * Not every property needs to be here - only high-value ones where developers commonly
 * forget to add the plugin. Third-party plugins can't be listed here anyway.
 *
 * ## Future Improvement
 *
 * A build-time script could generate these arrays from plugin manifests,
 * creating a single source of truth. For now, they're maintained manually.
 */
const KNOWN_COLUMN_PROPERTIES: InternalPropertyDefinition[] = [
  // EditingPlugin
  {
    property: 'editable',
    pluginName: 'editing',
    level: 'column',
    description: '',
    isUsed: (v) => v === true || typeof v === 'function',
  },
  { property: 'editor', pluginName: 'editing', level: 'column', description: '' },
  { property: 'editorParams', pluginName: 'editing', level: 'column', description: '' },
  // GroupingColumnsPlugin
  { property: 'group', pluginName: 'groupingColumns', level: 'column', description: '' },
  // PinnedColumnsPlugin
  {
    property: 'pinned',
    pluginName: 'pinnedColumns',
    level: 'column',
    description: '',
    isUsed: (v) => v === 'left' || v === 'right' || v === 'start' || v === 'end',
  },
];

/**
 * Static registry of known plugin-owned grid config properties.
 */
const KNOWN_CONFIG_PROPERTIES: InternalPropertyDefinition[] = [
  // EditingPlugin
  {
    property: 'rowEditable',
    pluginName: 'editing',
    level: 'config',
    description: '',
    isUsed: (v) => typeof v === 'function',
  },
  // GroupingColumnsPlugin
  {
    property: 'columnGroups',
    pluginName: 'groupingColumns',
    level: 'config',
    description: '',
    isUsed: (v) => Array.isArray(v) && v.length > 0,
  },
];
// #endregion

// #region Import Hints
/**
 * Convert a camelCase plugin name to kebab-case for import paths.
 * e.g. 'groupingRows' → 'grouping-rows', 'editing' → 'editing'
 */
function toKebabCase(s: string): string {
  return s.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
}

/**
 * Generate the feature-based import hint for a plugin from its name. The
 * feature-based API is the recommended approach: a side-effect import registers
 * the plugin, then it is toggled on via `gridConfig.features`.
 * e.g. 'editing' → "import '@toolbox-web/grid/features/editing';"
 */
function getFeatureImportHint(pluginName: string): string {
  return `import '@toolbox-web/grid/features/${toKebabCase(pluginName)}';`;
}

/**
 * Generate the plugin-based import hint for a plugin from its name.
 * e.g. 'editing' → "import { EditingPlugin } from '@toolbox-web/grid/plugins/editing';"
 */
function getPluginImportHint(pluginName: string): string {
  return `import { ${capitalize(pluginName)}Plugin } from '@toolbox-web/grid/plugins/${toKebabCase(pluginName)}';`;
}

/**
 * Whether the user is configuring the grid via the declarative `features` API
 * (as opposed to passing plugin instances through `plugins`). Diagnostics use
 * this to phrase hints in the same style the user is already writing — and to
 * nudge `plugins` users toward the recommended feature-based API.
 *
 * Note: the missing plugin's own config shape is intentionally NOT inspected
 * here — the core must stay ignorant of plugin internals, and the missing
 * plugin isn't loaded anyway. Hints show `true` (valid for toggle features) and
 * point to the docs for features that take a config object.
 */
function usesFeatureConfig(config: { features?: unknown } | undefined | null): boolean {
  const features = config?.features;
  return !!features && typeof features === 'object' && Object.keys(features as object).length > 0;
}
// #endregion

// #region Development Mode
// #endregion

// #region Helper Functions
/**
 * Helper to capitalize a plugin name for display.
 */
function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Check if a plugin with the given name is present in the plugins array.
 */
function hasPlugin(plugins: readonly BaseGridPlugin[], pluginName: string): boolean {
  return plugins.some((p) => p.name === pluginName);
}
// #endregion

// #region Property Validation
/**
 * Validate that column properties requiring plugins have those plugins loaded.
 *
 * @param config - The merged grid configuration
 * @param plugins - The array of loaded plugins
 * @throws Error if a plugin-owned property is used without the plugin
 */
export function validatePluginProperties<T>(
  config: GridConfig<T>,
  plugins: readonly BaseGridPlugin[],
  gridId?: string,
): void {
  // Use static registries of known plugin-owned properties
  const columnProps = KNOWN_COLUMN_PROPERTIES;
  const configProps = KNOWN_CONFIG_PROPERTIES;

  // Group errors by plugin to avoid spamming multiple errors
  const missingPlugins = new Map<string, { description: string; fields: string[]; isConfigProperty?: boolean }>();

  // Helper to add an error for a missing plugin
  function addError(pluginName: string, description: string, field: string, isConfigProperty = false) {
    if (!missingPlugins.has(pluginName)) {
      missingPlugins.set(pluginName, { description, fields: [], isConfigProperty });
    }
    // Entry is guaranteed to exist after the set above
    const entry = missingPlugins.get(pluginName)!;
    if (!entry.fields.includes(field)) {
      entry.fields.push(field);
    }
  }

  // Helper: explicit `features: { <name>: false }` is an intentional opt-out.
  // When the user explicitly disables a feature, we suppress "missing plugin"
  // diagnostics for that plugin's owned properties so column/config definitions
  // (e.g. `editor`, `editable`) can remain in place while the feature is toggled
  // off. The plugin can be re-enabled later without removing those properties.
  const features = (config as { features?: Record<string, unknown> }).features;
  function isExplicitlyDisabled(pluginName: string): boolean {
    return !!features && features[pluginName] === false;
  }

  // Validate grid config properties
  for (const def of configProps) {
    const value = (config as Record<string, unknown>)[def.property];
    const isUsed = def.isUsed ? def.isUsed(value) : value !== undefined;

    if (isUsed && !hasPlugin(plugins, def.pluginName) && !isExplicitlyDisabled(def.pluginName)) {
      const desc = def.description || `the "${def.property}" ${def.level} property`;
      addError(def.pluginName, desc, def.property, true);
    }
  }

  // Validate column properties
  const columns = config.columns;
  if (columns && columns.length > 0) {
    for (const column of columns) {
      for (const def of columnProps) {
        const value = (column as unknown as Record<string, unknown>)[def.property];
        // Use custom isUsed check if provided, otherwise check for defined value
        const isUsed = def.isUsed ? def.isUsed(value) : value !== undefined;

        if (isUsed && !hasPlugin(plugins, def.pluginName) && !isExplicitlyDisabled(def.pluginName)) {
          const field = (column as ColumnConfig).field || '<unknown>';
          const desc = def.description || `the "${def.property}" ${def.level} property`;
          addError(def.pluginName, desc, field);
        }
      }
    }
  }

  // Throw a single consolidated error if any missing plugins
  if (missingPlugins.size > 0) {
    const featureMode = usesFeatureConfig(config);
    const errors: string[] = [];
    for (const [pluginName, { description, fields, isConfigProperty }] of missingPlugins) {
      const fieldList = fields.slice(0, 3).join(', ') + (fields.length > 3 ? `, ... (${fields.length} total)` : '');
      const subject = isConfigProperty ? `Config uses ${description}` : `Column(s) [${fieldList}] use ${description}`;

      if (featureMode) {
        // User is on the recommended features API — phrase the hint the same way.
        errors.push(
          `${subject}, but the required feature is not enabled.\n` +
            `  → Enable it in gridConfig.features (pass \`true\` for defaults, or a config object — see the docs):\n` +
            `    ${getFeatureImportHint(pluginName)}\n` +
            `    features: { ${pluginName}: true }`,
        );
      } else {
        // User is passing plugin instances — match that style, then nudge to features.
        errors.push(
          `${subject}, but the required plugin is not loaded.\n` +
            `  → Add it to gridConfig.plugins:\n` +
            `    ${getPluginImportHint(pluginName)}\n` +
            `    plugins: [new ${capitalize(pluginName)}Plugin(), ...]\n` +
            `  → Tip: the feature-based API is recommended — ${getFeatureImportHint(pluginName)} then features: { ${pluginName}: true }`,
        );
      }
    }

    // Use MISSING_PLUGIN for column-level errors, MISSING_PLUGIN_CONFIG for config-level
    const code = [...missingPlugins.values()].some((e) => e.isConfigProperty) ? MISSING_PLUGIN_CONFIG : MISSING_PLUGIN;
    throwDiagnostic(
      code,
      `Configuration error:\n\n${errors.join('\n\n')}\n\n` +
        `This validation helps catch misconfigurations early. ` +
        `The properties listed above require their respective plugins to function.`,
      gridId,
    );
  }
}
// #endregion

// #region Config Rules Validation
/**
 * Validate plugin configuration rules declared in manifests.
 * Called after plugins are attached to check for invalid/conflicting configurations.
 *
 * Rules with severity 'error' throw an error.
 * Rules with severity 'warn' log a warning to console.
 *
 * @param plugins - The array of attached plugins (with config already merged)
 */
export function validatePluginConfigRules(plugins: readonly BaseGridPlugin[], gridId?: string): void {
  const errors: string[] = [];
  const warnings: string[] = [];

  for (const plugin of plugins) {
    const PluginClass = plugin.constructor as typeof BaseGridPlugin;
    const manifest = PluginClass.manifest as PluginManifest | undefined;
    if (!manifest?.configRules) continue;

    for (const rule of manifest.configRules) {
      // Access plugin's merged config via protected property
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const pluginConfig = (plugin as any).config;
      if (rule.check(pluginConfig)) {
        const formatted = `[${capitalize(plugin.name)}Plugin] Configuration warning: ${rule.message}`;
        if (rule.severity === 'error') {
          errors.push(formatted);
        } else {
          warnings.push(formatted);
        }
      }
    }
  }

  // Log warnings only in development (don't pollute production logs)
  if (warnings.length > 0 && isDevelopment()) {
    for (const warning of warnings) {
      warnDiagnostic(CONFIG_RULE_WARN, warning, gridId);
    }
  }

  // Throw consolidated error if any (always, regardless of environment)
  if (errors.length > 0) {
    throwDiagnostic(CONFIG_RULE_ERROR, `Configuration error:\n\n${errors.join('\n\n')}`, gridId);
  }
}
// #endregion

// #region Dependency Validation
/**
 * Validate plugin-to-plugin dependencies.
 * Called by PluginManager when attaching a new plugin.
 *
 * Dependencies are read from the plugin's static `dependencies` property.
 *
 * Each dependency may declare a `when` predicate (evaluated against the
 * depending plugin's resolved config) to make it config-conditional, and an
 * explicit `severity` (`'error'` | `'warn'` | `'info'`). When `severity` is
 * omitted, a missing hard dependency (`required !== false`) throws and a
 * missing soft dependency (`required: false`) is silent — preserving
 * backward-compatible behavior. `'warn'`/`'info'` messages are logged in
 * development only.
 *
 * @param plugin - The plugin instance being attached
 * @param loadedPlugins - The array of already-loaded plugins
 * @throws Error if a missing dependency resolves to `'error'` severity
 */
export function validatePluginDependencies(
  plugin: BaseGridPlugin,
  loadedPlugins: readonly BaseGridPlugin[],
  gridId?: string,
  config?: { features?: unknown } | null,
): void {
  const pluginName = plugin.name;
  const PluginClass = plugin.constructor as typeof BaseGridPlugin;

  // Get dependencies from plugin's static property
  const dependencies = PluginClass.dependencies ?? [];

  // Validate each dependency
  for (const dep of dependencies) {
    // Config-conditional dependency: skip when its predicate is not satisfied.
    if (dep.when && !dep.when(plugin.resolvedConfig)) continue;

    const requiredPlugin = dep.name;
    const required = dep.required ?? true; // Default to required
    const reason = dep.reason;
    const hasRequired = loadedPlugins.some((p) => p.name === requiredPlugin);

    if (hasRequired) continue;

    // Explicit severity wins; otherwise hard deps error and soft deps stay
    // silent (legacy behavior). `undefined` => no diagnostic.
    const severity = dep.severity ?? (required ? 'error' : undefined);
    if (!severity) continue;

    // Verb matches intent: errors "require", soft notifications "recommend".
    const verb = severity === 'error' ? 'requires' : 'recommends';
    const reasonText = reason ?? `${capitalize(pluginName)}Plugin ${verb} ${capitalize(requiredPlugin)}Plugin`;

    if (severity === 'error') {
      if (usesFeatureConfig(config)) {
        // Feature API: order is irrelevant (topology sorts it), but keep the
        // required feature first so the example reads naturally.
        throwDiagnostic(
          MISSING_DEPENDENCY,
          `Plugin dependency error:\n\n` +
            `${reasonText}.\n\n` +
            `  → Enable the required feature in gridConfig.features ` +
            `(pass \`true\` for defaults, or a config object — see the docs):\n` +
            `    ${getFeatureImportHint(requiredPlugin)}\n` +
            `    ${getFeatureImportHint(pluginName)}\n` +
            `    features: {\n` +
            `      ${requiredPlugin}: true,\n` +
            `      ${pluginName}: true,\n` +
            `    }`,
          gridId,
        );
      } else {
        // Plugin instances: order matters — the dependency must be listed first.
        throwDiagnostic(
          MISSING_DEPENDENCY,
          `Plugin dependency error:\n\n` +
            `${reasonText}.\n\n` +
            `  → Add the required plugin to gridConfig.plugins BEFORE ${capitalize(pluginName)}Plugin:\n` +
            `    ${getPluginImportHint(requiredPlugin)}\n` +
            `    plugins: [new ${capitalize(requiredPlugin)}Plugin(), new ${capitalize(pluginName)}Plugin()]\n` +
            `  → Tip: the feature-based API is recommended and orders dependencies for you — ` +
            `features: { ${requiredPlugin}: true, ${pluginName}: true }`,
          gridId,
        );
      }
    } else if (isDevelopment()) {
      // Soft notification — development only to avoid polluting production logs.
      // Use OPTIONAL_DEPENDENCY (TBW021), not the required-dependency code.
      if (severity === 'warn') {
        warnDiagnostic(OPTIONAL_DEPENDENCY, `${reasonText}.`, gridId);
      } else {
        debugDiagnostic(OPTIONAL_DEPENDENCY, `${reasonText}.`, gridId);
      }
    }
  }
}
// #endregion

// #region Incompatibility Validation
/**
 * Validate that no incompatible plugins are loaded together.
 * Called after all plugins are attached to the grid.
 *
 * Incompatibilities are read from each plugin's manifest `incompatibleWith` property.
 * When a conflict is detected, a warning is logged (in development mode).
 *
 * @param plugins - All attached plugins
 */
export function validatePluginIncompatibilities(plugins: readonly BaseGridPlugin[], gridId?: string): void {
  // Only warn in development mode to avoid polluting production logs
  if (!isDevelopment()) return;

  const pluginNames = new Set(plugins.map((p) => p.name));
  const warned = new Set<string>(); // Avoid duplicate warnings for symmetric conflicts

  for (const plugin of plugins) {
    const PluginClass = plugin.constructor as typeof BaseGridPlugin;
    const manifest = PluginClass.manifest as PluginManifest | undefined;
    if (!manifest?.incompatibleWith) continue;

    for (const incompatibility of manifest.incompatibleWith) {
      if (pluginNames.has(incompatibility.name)) {
        // Create a symmetric key to avoid warning twice (A→B and B→A)
        const key = [plugin.name, incompatibility.name].sort().join('↔');
        if (warned.has(key)) continue;
        warned.add(key);

        warnDiagnostic(
          INCOMPATIBLE_PLUGINS,
          `${capitalize(plugin.name)}Plugin and ${capitalize(incompatibility.name)}Plugin are both loaded, ` +
            `but they are currently incompatible.\n\n` +
            `  → ${incompatibility.reason}\n\n` +
            `  Consider removing one of these plugins to avoid unexpected behavior.`,
          gridId,
        );
      }
    }
  }
}
// #endregion
